import { useForm, ValidationError } from '@formspree/react';
import { useTranslation } from 'react-i18next';
import { Mail, Building2, MapPin, User } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import Card from '../components/ui/Card';
import Container from '../components/ui/Container';
import SectionHeader from '../components/ui/SectionHeader';

const ForClinics = () => {
    const { t } = useTranslation();
    const [state, handleSubmit] = useForm("mvgaadlo");

    if (state.succeeded) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
                <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
                    <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">
                        {t('clinics.thankYou')}
                    </h2>
                    <p className="text-center text-gray-600">
                        {t('clinics.thankYouMessage')}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>{t('clinics.title')} | SafeTripVax</title>
                <meta name="description" content={t('clinics.description')} />
            </Helmet>

            <div className="min-h-screen bg-gray-50 py-12">
                <Container className="max-w-7xl px-4 sm:px-6 lg:px-8">
                    <SectionHeader>
                        <h1 className="text-4xl font-bold text-gray-900 mb-4">
                            {t('clinics.title')}
                        </h1>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            {t('clinics.subtitle')}
                        </p>
                    </SectionHeader>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Contact Information */}
                        <Card>
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('clinics.info.title')}</h2>
                            <div className="space-y-4">
                                <div className="flex items-center text-gray-600">
                                    <Mail className="w-5 h-5 mr-3 text-blue-600" />
                                    <a href="mailto:biuro@epko.tech" className="hover:text-blue-600 transition-colors">
                                        biuro@epko.tech
                                    </a>
                                </div>
                            </div>
                        </Card>

                        {/* Contact Form */}
                        <Card>
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">
                                {t('clinics.formTitle')}
                            </h2>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label htmlFor="clinicName" className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('clinics.clinicName')}
                                    </label>
                                    <input
                                        type="text"
                                        id="clinicName"
                                        name="clinicName"
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <ValidationError prefix="Clinic Name" field="clinicName" errors={state.errors} />
                                </div>

                                <div>
                                    <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('clinics.location')}
                                    </label>
                                    <input
                                        type="text"
                                        id="location"
                                        name="location"
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <ValidationError prefix="Location" field="location" errors={state.errors} />
                                </div>

                                <div>
                                    <label htmlFor="ownerName" className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('clinics.ownerName')}
                                    </label>
                                    <input
                                        type="text"
                                        id="ownerName"
                                        name="ownerName"
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <ValidationError prefix="Owner Name" field="ownerName" errors={state.errors} />
                                </div>

                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('clinics.email')}
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <ValidationError prefix="Email" field="email" errors={state.errors} />
                                </div>

                                <div>
                                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('clinics.message')}
                                    </label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        required
                                        rows={4}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    ></textarea>
                                    <ValidationError prefix="Message" field="message" errors={state.errors} />
                                </div>

                                <button
                                    type="submit"
                                    disabled={state.submitting}
                                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {state.submitting ? t('clinics.sending') : t('clinics.send')}
                                </button>
                            </form>
                        </Card>
                    </div>
                </Container>
            </div>
        </>
    );
};

export default ForClinics; 